import { Injectable } from "@angular/core";
import {
    AircraftEntity,
    FlightPositionUpdate,
    FlightPositionUpdateType,
    MissionPlanRouteSection,
    PinData,
    RouteAreaTypeId,
    RouteData,
    Trajectory,
    Waypoint,
} from "@dtm-frontend/shared/ui";
import { DateUtils, FunctionUtils, MILLISECONDS_IN_SECOND, ObjectUtils } from "@dtm-frontend/shared/utils";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import turfDistance from "@turf/distance";
import { Feature, LineString, MultiPolygon, Point, Polygon, featureCollection, lineString, point } from "@turf/helpers";
import lineIntersect from "@turf/line-intersect";
import lineSlice from "@turf/line-slice";
import midpoint from "@turf/midpoint";
import nearestPointOnLine from "@turf/nearest-point-on-line";
import { Cartesian3, Cartographic } from "cesium";
import { combineFlightArea } from "../../../shared/utils/combine-flight-area";
import { CoordinateConverter, MapUtils, RouteEntity } from "../../index";

interface FlightUpdateAcEntity {
    entity: AircraftEntity;
    actionType: "ADD_OR_UPDATE" | "REMOVE";
    id: string;
}

@Injectable()
export class RouteViewerService<T> {
    constructor(private readonly coordinateConverter: CoordinateConverter) {}

    public transformRouteDataToFlightAreaEntities({
        route,
        sectionStatuses,
        isCollision,
        isMain,
        isSelected,
        emergency,
    }: RouteData<T>): RouteEntity[] {
        return route.sections.flatMap((section, sectionIndex) => {
            const flightZoneArea = section.flightZone?.flightArea.volume.area.coordinates ?? [];
            const segmentArea = section.segment?.flightArea.volume.area.coordinates ?? [];
            const coordinates = [...flightZoneArea, ...segmentArea].map((positions) => positions.map(([x, y]) => [x, y]));

            return coordinates.map((degreesPositions) => ({
                id: "flightArea",
                positions: Cartesian3.fromDegreesArray(degreesPositions.flat()),
                degreesPositions,
                segmentStatus: sectionStatuses?.[sectionIndex],
                isMain,
                isSelected,
                isCollision,
                isEmergency: !!emergency,
            }));
        });
    }

    public transformRouteDataToPathEntities({
        route,
        sectionStatuses,
        isCollision,
        isMain,
        isSelected,
        emergency,
    }: RouteData<T>): RouteEntity[] {
        return route.sections
            .map((section, sectionIndex) => {
                const segment = section.segment;
                if (!segment) {
                    return;
                }
                const positions = [
                    segment.fromWaypoint.point.longitude,
                    segment.fromWaypoint.point.latitude,
                    segment.toWaypoint.point.longitude,
                    segment.toWaypoint.point.latitude,
                ];

                return {
                    id: "path",
                    positions: Cartesian3.fromDegreesArray(positions),
                    degreesPositions: [positions],
                    segmentStatus: sectionStatuses?.[sectionIndex],
                    isMain,
                    isSelected,
                    isCollision,
                    isEmergency: !!emergency,
                } satisfies RouteEntity;
            })
            .filter(FunctionUtils.isTruthy);
    }

    public transformRouteDataToWaypointEntities(
        { route, isPathBased, sectionStatuses }: RouteData<T>,
        drawableFeatures: RouteAreaTypeId[] = ["path", "flightArea"]
    ): RouteEntity[] {
        const waypoints = route.sections.reduce<Map<string, { waypoint: Waypoint; show: boolean }>>((output, section) => {
            const centerPoint = section.flightZone?.center;
            const fromPoint = section.segment?.fromWaypoint;
            const toPoint = section.segment?.toWaypoint;

            if (centerPoint) {
                output.set(centerPoint.name, { waypoint: centerPoint, show: !!section.segment });
            }
            if (fromPoint) {
                output.set(fromPoint.name, { waypoint: fromPoint, show: !!section.segment });
            }
            if (toPoint) {
                output.set(toPoint.name, { waypoint: toPoint, show: !!section.segment });
            }

            return output;
        }, new Map<string, { waypoint: Waypoint; show: boolean }>());

        return [...waypoints.values()]
            .map(({ waypoint, show }, index) => {
                if (!isPathBased) {
                    return;
                }

                const pointEntity = {
                    id: "waypoint",
                    positions: [Cartesian3.fromDegrees(waypoint.point.longitude, waypoint.point.latitude)],
                    segmentStatus: sectionStatuses?.[index],
                    data: { isTakeoff: index === 0, isLanding: index === waypoints.size - 1, show },
                } satisfies RouteEntity;

                return drawableFeatures.includes("path") ||
                    ((pointEntity.data.isTakeoff || pointEntity.data.isLanding) && drawableFeatures.includes("flightArea"))
                    ? pointEntity
                    : undefined;
            })
            .filter(FunctionUtils.isTruthy);
    }

    public mapRouteToPinEntities(
        { route: { sections }, isSelected, isMain, isCollision, sectionStatuses }: RouteData<T>,
        drawableFeatures: RouteAreaTypeId[]
    ) {
        return sections
            .reduce<PinData<T>[]>((previousValue, section) => {
                if (section.flightZone) {
                    previousValue.push({
                        flightArea: section.flightZone.flightArea,
                        waypoints: [section.flightZone.center],
                    });
                }

                if (section.segment) {
                    previousValue.push({
                        flightArea: section.segment.flightArea,
                        waypoints: [section.segment.fromWaypoint, section.segment.toWaypoint],
                    });
                }

                return previousValue;
            }, [])
            .map((data, index) => {
                const pinData = ObjectUtils.cloneDeep(data);

                if (!drawableFeatures.includes("flightArea")) {
                    return;
                }

                let pinXPosition;
                let pinYPosition;

                if (pinData.waypoints.length > 1) {
                    const waypointFrom = pinData.waypoints[0].point;
                    const waypointTo = pinData.waypoints[1].point;

                    const pointPositions = midpoint(
                        [waypointFrom.longitude, waypointFrom.latitude],
                        [waypointTo.longitude, waypointTo.latitude]
                    ).geometry.coordinates;
                    pinXPosition = pointPositions[0];
                    pinYPosition = pointPositions[1];
                } else {
                    pinXPosition = pinData.waypoints[0].point.longitude;
                    pinYPosition = pinData.waypoints[0].point.latitude;

                    const estimateArrivalTime = new Date(pinData.waypoints[0].estimatedArriveAt.max).getTime();
                    const stopoverDurationSeconds = sections[0].flightZone
                        ? DateUtils.convertISO8601DurationToSeconds(sections[0].flightZone.stopover.max)
                        : undefined;
                    const durationTime = (stopoverDurationSeconds ?? 0) * MILLISECONDS_IN_SECOND;

                    pinData.waypoints[0].estimatedArriveAt.max = new Date(estimateArrivalTime + durationTime);
                }

                const position = Cartographic.toCartesian(this.coordinateConverter.degreesToCartographic(pinXPosition, pinYPosition));

                return {
                    data: { ...pinData, isMain, isSelected, isCollision, status: sectionStatuses?.[index], index },
                    position,
                };
            })
            .filter(FunctionUtils.isTruthy);
    }

    public transformFlightUpdateToFlightEntity(update: FlightPositionUpdate): FlightUpdateAcEntity {
        const { position } = update;
        const positionCartesian = Cartesian3.fromDegrees(position.longitude, position.latitude, 0);

        const aircraftEntity: AircraftEntity = {
            name: update.callSign,
            aircraftType: update.aircraftType,
            updateTime: new Date().getTime(),
            isConnectionLost: update.isConnectionLost,
            isFaradaSource: update.isFaradaSource,
            trackerIdentifier: update.trackerIdentifier,
            position: { ...position, positionCartesian },
            sourceType: update.sourceType,
            operator: update.operator,
            operatorId: update.operatorId,
        };

        return {
            entity: aircraftEntity,
            actionType: update.updateType === FlightPositionUpdateType.End ? "REMOVE" : "ADD_OR_UPDATE",
            id: update.id,
        };
    }

    public transformRouteDataToBlockPinEntity(
        { route: { sections }, isPathBased }: RouteData<T>,
        drawableFeatures: RouteAreaTypeId[] = ["blockers"]
    ) {
        if (!isPathBased || !drawableFeatures.includes("blockers")) {
            return;
        }

        const blockPoint = this.getBlockPoint(sections);

        if (!blockPoint) {
            return;
        }

        return {
            id: "blockPin",
            position: Cartographic.toCartesian(
                this.coordinateConverter.degreesToCartographic(blockPoint.geometry.coordinates[0], blockPoint.geometry.coordinates[1])
            ),
        };
    }

    private getBlockPoint([firstSection, nextSection]: MissionPlanRouteSection[]) {
        if (nextSection?.segment) {
            return point([nextSection.segment.fromWaypoint.point.longitude, nextSection.segment.fromWaypoint.point.latitude]);
        }

        if (!nextSection || !nextSection.flightZone) {
            return;
        }

        const nextSectionAreaLine = lineString(nextSection.flightZone.flightArea.volume.area.coordinates.flat());

        let currentPoint: Feature<Point>;

        if (firstSection.segment) {
            currentPoint = point([firstSection.segment.toWaypoint.point.longitude, firstSection.segment.toWaypoint.point.latitude]);
        } else if (firstSection.flightZone) {
            currentPoint = point([firstSection.flightZone.center.point.longitude, firstSection.flightZone.center.point.latitude]);
        } else {
            return;
        }

        return nearestPointOnLine(nextSectionAreaLine, currentPoint);
    }

    public transformRouteDataToTrajectoryEntitiesPositions(trajectories: Trajectory[]) {
        return trajectories.map((trajectory) =>
            trajectory.positions.map(({ longitude, latitude }) =>
                MapUtils.convertSerializableCartographicToCartesian3({ longitude, latitude })
            )
        );
    }

    public transformRouteDataToMarkedTrajectoryEntitiesPositions(
        trajectories: Trajectory[],
        routeData?: RouteData<T>
    ): { positions: Cartesian3[]; isMarked: boolean; isActive?: boolean }[][] {
        if (!routeData) {
            return this.transformRouteDataToTrajectoryEntitiesPositions(trajectories).map((positions) => [
                {
                    positions,
                    isMarked: false,
                },
            ]);
        }

        const sections = routeData?.route.sections;

        if (!sections?.length || !trajectories.length) {
            return [];
        }
        const combinedFlightArea = combineFlightArea(sections);

        if (!combinedFlightArea) {
            return [];
        }

        return trajectories.map((trajectory) => {
            if (trajectory.positions.length < 2) {
                return [];
            }

            const trajectoryLine = lineString(trajectory.positions.map(({ longitude, latitude }) => [longitude, latitude]));
            const orderedIntersectionPoints = this.findOrderedIntersectionPoints(trajectory, combinedFlightArea);
            const firstPoint = trajectoryLine.geometry.coordinates[0];
            const lastPoint = trajectoryLine.geometry.coordinates[trajectoryLine.geometry.coordinates.length - 1];
            const isFirstPointInArea = booleanPointInPolygon(point(firstPoint), combinedFlightArea);
            const combinedIntersectionPoints = [point(firstPoint), ...orderedIntersectionPoints.features, point(lastPoint)];

            const markedLines = combinedIntersectionPoints
                .reduce<Feature<LineString>[]>((acc, value, pointIndex, array) => {
                    const nextPoint = array[pointIndex + 1];

                    return !nextPoint ? acc : [...acc, lineSlice(value, nextPoint, trajectoryLine)];
                }, [])
                .map((line, lineIndex) => ({ isMarked: lineIndex % 2 === 0 ? !isFirstPointInArea : isFirstPointInArea, line }));

            return markedLines.map(({ isMarked, line }) => ({
                positions: line.geometry.coordinates.map(([longitude, latitude]) =>
                    MapUtils.convertSerializableCartographicToCartesian3({ longitude, latitude })
                ),
                isMarked,
                isActive: trajectory.isActive,
            }));
        });
    }

    private findOrderedIntersectionPoints(trajectory: Trajectory, area: Feature<Polygon | MultiPolygon>) {
        const orderedIntersectionPoints = featureCollection<Point>([]);

        trajectory.positions.forEach((position, positionIndex) => {
            const nextPosition = trajectory.positions[positionIndex + 1];
            if (!nextPosition) {
                return;
            }

            const currentPointCoords = [position.longitude, position.latitude];
            const nextPointCoords = [nextPosition.longitude, nextPosition.latitude];

            const lineSection = lineString([currentPointCoords, nextPointCoords]);
            const intersectionPoints = lineIntersect(area.geometry, lineSection);

            if (intersectionPoints.features.length > 1) {
                const currentPoint = point(currentPointCoords);
                intersectionPoints.features.sort((left, right) => turfDistance(left, currentPoint) - turfDistance(right, currentPoint));
            }

            orderedIntersectionPoints.features.push(...intersectionPoints.features);
        });

        return orderedIntersectionPoints;
    }
}
